import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import DocumentationTree from './DocumentationTree';
import RoleBadge from './RoleBadge';


const NavigationSidebar = ({ 
  isCollapsed = false, 
  onToggle, 
  // userRole = 'admin',
  isAuthenticated = true,
  onLogout 
}) => {
  const location = useLocation();
  const [isMainMenuExpanded, setIsMainMenuExpanded] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [userRole, setUserRole] = useState(null);
const [loadingRole, setLoadingRole] = useState(true);

  const getRoleFromStorage = () => {
    const storedRole = localStorage.getItem('role'); // will be "1" | "2" | "3"
    switch (storedRole) {
      case "1":
        return "admin";
      case "3":
        return "client";
      case "4":
        return "courier";
      default:
        return "guest";
    }
  };

   useEffect(() => {
  setUserRole(getRoleFromStorage());
  setLoadingRole(false);
}, [location.pathname]); // Re-run when route changes

  const navigationItems = [
    {
      section: 'Documentation',
      items: [
        {
          label: 'API Documentation Hub',
          path: '/api-documentation-hub',
          icon: 'BookOpen',
          roles: ['admin', 'client']
        }
      ]
    },
    {
      section: 'API Management',
      items: [
        {
          label: 'API Key Management',
          path: '/api-key-management',
          icon: 'Key',
          roles: ['admin', 'client']
        }
      ]
    },
    {
      section: 'Administration',
      items: [
        {
          label: 'Client Management',
          path: '/client-management',
          icon: 'Users',
          roles: ['admin']
        },
        // {
        //   label: 'Webhook Management',
        //   path: '/webhook-management',
        //   icon: 'Webhook',
        //   roles: ['admin', 'client']
        // },
        {
          label: 'Product Categories',
          path: '/product-categories-management',
          icon: 'Package',
          roles: ['admin', 'client']
        },
       {
          label: 'Product Management',
          path: '/product-management',
          icon: 'Map',
          roles: ['client']
        }
      ]
    }
  ];

  const toggleMainMenu = () => {
    setIsMainMenuExpanded(prev => !prev);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  const isItemActive = (path) => {
    return location.pathname === path;
  };

  const hasAccessToItem = (roles) => {
    return roles?.includes(userRole);
  };

  const filteredNavigation = navigationItems?.map(section => ({
    ...section,
    items: section?.items?.filter(item => hasAccessToItem(item?.roles))
  }))?.filter(section => section?.items?.length > 0);

  if (!isAuthenticated && location.pathname !== '/admin-login') {
    return null;
  }

  if (loadingRole) {
  return (
    <aside className="fixed top-0 left-0 h-full w-80 bg-card border-r border-border z-100 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
    </aside>
  );
}

  return (
    <>
      {/* Mobile Backdrop */}
      {!isCollapsed && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-90"
          onClick={onToggle}
        />
      )}
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full bg-card border-r border-border z-100
        transition-transform duration-300 ease-in-out
        ${isCollapsed ? '-translate-x-full lg:translate-x-0' : 'translate-x-0'}
        ${isCollapsed ? 'lg:w-16' : 'w-80 lg:w-80'}
      `}>
        <div className="flex flex-col h-full">
                {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-border">
        {!isCollapsed && (
          <div className="flex items-center">
            <img 
              src="/assets/logo/lakeelogo.png" 
              alt="Lakeee Shop Logo" 
              className="w-25 h-12 object-contain" 
            />
          </div>
        )}
        
        <button
          onClick={onToggle}
          className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
        >
          <Icon name="X" size={20} />
        </button>
          </div>


           {/* 🔹 Role Badge */}
          {!isCollapsed && isAuthenticated && userRole && (
            <div className="px-6 py-4">
              <RoleBadge role={userRole} />
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto px-4 py-2">
            {/* Main Menu */}
            <div className="mb-6">
              {!isCollapsed && (
                <button
                  onClick={toggleMainMenu}
                  className="flex items-center justify-between w-full px-2 py-3 text-base font-semibold text-foreground hover:bg-muted transition-colors rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <Icon name="Menu" size={18} />
                    <span>Main Menu</span>
                  </div>
                  <Icon 
                    name={isMainMenuExpanded ? "ChevronDown" : "ChevronRight"} 
                    size={16} 
                  />
                </button>
              )}
              
              {/* Sub Menus - All sections under main menu */}
              <div className={`
                ml-2 space-y-4 mt-3
                ${isCollapsed ? 'block' : isMainMenuExpanded ? 'block' : 'hidden'}
              `}>
                {filteredNavigation?.map((section) => (
                  <div key={section?.section} className="space-y-2">
                    {!isCollapsed && (
                      <button
                        onClick={() => toggleSection(section?.section)}
                        className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted/50"
                      >
                        <span>{section?.section}</span>
                        <Icon 
                          name={expandedSections?.[section?.section] ? "ChevronDown" : "ChevronRight"} 
                          size={14} 
                        />
                      </button>
                    )}
                    
                    <div className={`
                      space-y-1 ml-2
                      ${isCollapsed ? 'block' : expandedSections?.[section?.section] !== false ? 'block' : 'hidden'}
                    `}>
                      {section?.items?.map((item) => (
                        <Link
                          key={item?.path}
                          to={item?.path}
                          className={`
                            flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-smooth
                            ${isItemActive(item?.path)
                              ? 'bg-primary text-primary-foreground shadow-soft'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                            }
                            ${isCollapsed ? 'justify-center' : 'justify-start space-x-3'}
                          `}
                          title={isCollapsed ? item?.label : ''}
                        >
                          <Icon name={item?.icon} size={18} />
                          {!isCollapsed && <span>{item?.label}</span>}
                        </Link>
                      ))}
                    </div>

                    {/* Documentation Tree for API Documentation Hub */}
                    {section?.section === 'Documentation' && 
                     !isCollapsed && 
                     (expandedSections?.[section?.section] !== false) && 
                     location.pathname === '/api-documentation-hub' && (
                      <div className="ml-6 mt-2">
                        <DocumentationTree />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </nav>

          {/* Footer Actions */}
          {!isCollapsed && isAuthenticated && (
            <div className="p-4 border-t border-border">
              <div className="space-y-2">
                <Link
                  to="/admin-login"
                  onClick={onLogout}
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
                >
                  <Icon name="LogOut" size={18} />
                  <span>Sign Out</span>
                </Link>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default NavigationSidebar;